"use client";

import { BarChart3, Lock } from "lucide-react";
import { useId } from "react";
import type { FeedOriginalPost, FeedPost } from "@/actions/post";
import { FeedVideo } from "@/components/feed/feed-video";
import { SharedStickerEmbed } from "@/components/stickers/shared-sticker-embed";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { MediaSoundButton } from "@/components/ui/media-sound-button";
import { clampFeedMediaAspectRatio } from "@/lib/aspect-ratios";
import { formatRelativeTimeCompact } from "@/lib/dates/relative-time";
import { toRenderableMedia } from "@/lib/media";
import { cn } from "@/lib/utils";
import { sharedStickerLabel } from "@/lib/wallet/sticker-share";
import { FeedEntityQuoteEmbed } from "./feed-entity-quote-embed";
import { PostEventTag } from "./post-event-tag";
import { PostLocationTag } from "./post-location-tag";
import { RichPostText } from "./rich-post-text";

type OriginalPostEmbedProps = {
  originalPost: FeedOriginalPost | FeedPost;
  compact?: boolean;
  /** `row`: X's thumbnail-left quote, for a quoting post with its own media. */
  layout?: "card" | "row";
};

/**
 * The referenced-post card shared by feed quote posts and the quote composer.
 * Twitter-style: a compact bordered card where the text leads and media stays
 * trimmed, so the quote author's own words keep the spotlight. It deliberately
 * mirrors the post's rich payload instead of flattening the target to plain
 * text: text, media, polls and place/context chips all survive.
 */
export function OriginalPostEmbed({
  originalPost,
  compact = false,
  layout = "card",
}: OriginalPostEmbedProps) {
  const mediaAudioSeed = useId();
  const firstMedia = originalPost.media
    .map((item) => toRenderableMedia(item))
    .find((item): item is NonNullable<ReturnType<typeof toRenderableMedia>> =>
      Boolean(item),
    );
  const isUnavailable =
    "isUnavailable" in originalPost && Boolean(originalPost.isUnavailable);
  const isRestricted =
    "isRestricted" in originalPost && Boolean(originalPost.isRestricted);

  // Restricted: the original exists but this viewer isn't in its audience.
  // Explain why (never show content) and point them at the author's profile.
  if (isRestricted) {
    const reason =
      "restrictedReason" in originalPost &&
      originalPost.restrictedReason === "close_friends"
        ? `Only ${originalPost.author.displayName}'s close friends can see this post.`
        : `Only ${originalPost.author.displayName}'s buddies can see this post.`;
    const profileHref = originalPost.author.username
      ? `/profile/${originalPost.author.username}`
      : null;

    return (
      <div
        className="overflow-hidden rounded-2xl border border-border bg-surface-alt/35"
        data-testid="original-post-embed-restricted"
      >
        <div
          className={cn("flex items-start gap-2.5", compact ? "p-2.5" : "p-3")}
        >
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-tertiary">
            <Lock className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text-secondary">{reason}</p>
            {profileHref ? (
              <BackAwareLink
                href={profileHref}
                onClick={(event) => event.stopPropagation()}
                className="mt-0.5 inline-block text-sm font-semibold text-brand hover:underline"
              >
                View @{originalPost.author.username}'s profile
              </BackAwareLink>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const renderableMedia = originalPost.media
    .map((item) => toRenderableMedia(item))
    .filter((item): item is NonNullable<ReturnType<typeof toRenderableMedia>> =>
      Boolean(item),
    );
  const isMediaStrip = renderableMedia.length > 1;
  // A single item keeps its true (clamped) shape instead of a 16:9 letterbox,
  // which visibly squished portrait photos and videos.
  const mediaAspect = clampFeedMediaAspectRatio(
    firstMedia?.width && firstMedia.height
      ? firstMedia.width / firstMedia.height
      : undefined,
  );
  const postHref = `/post/${originalPost.id}`;
  const authorLine = (
    <div className="flex min-w-0 items-center gap-1.5 text-[14px] leading-5">
      <span className="relative h-5 w-5 shrink-0 overflow-hidden rounded-[30%] bg-surface-alt">
        {originalPost.author.avatarUrl ? (
          <Image
            src={originalPost.author.avatarUrl}
            alt=""
            fill
            className="object-cover"
            sizes="20px"
          />
        ) : null}
      </span>
      <span className="min-w-0 shrink truncate font-semibold text-text-primary">
        {originalPost.author.displayName}
      </span>
      {originalPost.author.username ? (
        <span className="min-w-0 shrink-[2] truncate text-text-secondary">
          @{originalPost.author.username}
        </span>
      ) : null}
      <span className="flex shrink-0 items-center gap-1 text-text-secondary">
        <span aria-hidden>·</span>
        <time
          dateTime={new Date(originalPost.createdAt).toISOString()}
          suppressHydrationWarning
        >
          {formatRelativeTimeCompact(new Date(originalPost.createdAt))}
        </time>
      </span>
    </div>
  );

  // X's compact quote: when the quoting post already carries its own media, the
  // referenced post shrinks to a row — thumbnail left, words right — so two
  // big media frames never stack.
  if (layout === "row") {
    return (
      <BackAwareLink
        href={postHref}
        data-testid="original-post-embed"
        className={cn(
          "block overflow-hidden rounded-2xl border border-border transition-colors hover:bg-surface-alt/40",
          isUnavailable && "pointer-events-none opacity-70",
        )}
      >
        <div className="flex flex-col gap-1 px-3 pt-2.5">{authorLine}</div>
        <div className="flex gap-3 p-3 pt-1.5">
          {firstMedia ? (
            <span className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-surface-alt">
              <EmbedMediaTile
                item={firstMedia}
                audioId={`${mediaAudioSeed}-${firstMedia.id}`}
                forceMuted
              />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            {originalPost.content ? (
              <RichPostText
                content={originalPost.content}
                mentions={originalPost.mentions}
                className="text-[15px] leading-5 text-text-primary"
                lineClamp={firstMedia ? 4 : 3}
                linkMentions={false}
                linkUrls={false}
              />
            ) : originalPost.poll ? (
              <p className="line-clamp-3 text-[15px] leading-5 text-text-primary">
                {originalPost.poll.question}
              </p>
            ) : originalPost.sticker && !firstMedia ? (
              <p className="text-sm text-text-secondary">
                {sharedStickerLabel(originalPost.sticker.stickerId)}
              </p>
            ) : (
              <p className="text-sm text-text-secondary">
                {firstMedia?.kind === "video" ? "Video" : "Photo"}
              </p>
            )}
          </div>
        </div>
      </BackAwareLink>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border transition-colors has-[a:hover]:bg-surface-alt/40",
        isUnavailable && "opacity-70",
      )}
      data-testid="original-post-embed"
    >
      <BackAwareLink
        href={postHref}
        className={cn("block", isUnavailable && "pointer-events-none")}
      >
        <div
          className={cn(
            "flex flex-col gap-1",
            compact ? "px-2.5 pt-2.5 pb-2" : "px-3 pt-3 pb-2.5",
          )}
        >
          {authorLine}

          {originalPost.content ? (
            <RichPostText
              content={originalPost.content}
              mentions={originalPost.mentions}
              className="text-[15px] leading-5 text-text-primary"
              lineClamp={compact ? 3 : 6}
              linkMentions={false}
              linkUrls={false}
            />
          ) : null}

          {originalPost.poll ? (
            <div className="mt-1 space-y-1.5 rounded-xl border border-border p-2.5">
              <div className="flex items-start gap-2">
                <BarChart3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                <RichPostText
                  content={originalPost.poll.question}
                  mentions={originalPost.poll.questionMentions}
                  className="text-sm leading-snug font-semibold"
                  lineClamp={2}
                  linkMentions={false}
                  linkUrls={false}
                />
              </div>
              <div className="grid gap-1">
                {originalPost.poll.options
                  .slice(0, compact ? 3 : 4)
                  .map((option) => (
                    <span
                      key={option.id}
                      className="rounded-lg border border-border px-2.5 py-1.5 text-xs text-text-secondary"
                    >
                      {option.label}
                    </span>
                  ))}
              </div>
            </div>
          ) : null}
        </div>
      </BackAwareLink>

      {/* Media sits outside the text link so a video can carry its own volume
          toggle (a button can't legally nest inside the anchor). It still opens
          the post on tap; the volume button is a sibling that stops the tap. */}
      {isMediaStrip ? (
        // Several items: the same sideways strip as the timeline, scaled down
        // — one height, each card at its own shape, the next one peeking in.
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto overscroll-x-contain px-3 pb-3">
          {renderableMedia.map((item) => {
            const itemAspect =
              item.width && item.height ? item.width / item.height : 0.8;
            return (
              <BackAwareLink
                key={item.id}
                href={postHref}
                aria-label="Open referenced post"
                className={cn(
                  "relative h-[200px] max-w-[75%] shrink-0 overflow-hidden rounded-xl border border-border bg-surface-alt",
                  isUnavailable && "pointer-events-none",
                )}
                style={{
                  aspectRatio: `${Math.min(1.5, Math.max(0.6, itemAspect))} / 1`,
                }}
              >
                <EmbedMediaTile
                  item={item}
                  audioId={`${mediaAudioSeed}-${item.id}`}
                  forceMuted
                />
              </BackAwareLink>
            );
          })}
        </div>
      ) : firstMedia ? (
        <div className="relative">
          <BackAwareLink
            href={postHref}
            aria-label="Open referenced post"
            className={cn("block", isUnavailable && "pointer-events-none")}
          >
            <div
              className="relative w-full overflow-hidden bg-surface-alt"
              style={{ aspectRatio: `${mediaAspect} / 1` }}
            >
              <EmbedMediaTile
                item={firstMedia}
                audioId={`${mediaAudioSeed}-${firstMedia.id}`}
                // Deeply nested (compact) embeds stay muted with no control;
                // a primary repost lets the viewer unmute the reference.
                forceMuted={compact}
              />
            </div>
          </BackAwareLink>
          {firstMedia.kind === "video" &&
          firstMedia.provider !== "klipy" &&
          !compact ? (
            <MediaSoundButton audioId={`${mediaAudioSeed}-${firstMedia.id}`} />
          ) : null}
        </div>
      ) : null}

      {/* The live sticker is its own button, so it sits outside the link. */}
      {originalPost.sticker && !isUnavailable ? (
        <div className={cn(compact ? "px-2.5 pb-2.5" : "px-3 pb-3")}>
          <SharedStickerEmbed
            stickerId={originalPost.sticker.stickerId}
            owner={{
              id: originalPost.author.id,
              name: originalPost.author.displayName,
            }}
            width={compact ? 120 : 150}
          />
        </div>
      ) : null}

      {/* Tags stay outside the card link: the pills carry their own links. */}
      {originalPost.contextTag || originalPost.locationTag ? (
        <div
          className={cn(
            "flex flex-wrap gap-1.5",
            compact ? "px-2.5 pb-2.5" : "px-3 pb-3",
            firstMedia && "pt-2",
          )}
        >
          {originalPost.contextTag ? (
            <PostEventTag tag={originalPost.contextTag} compact asLink />
          ) : null}
          {originalPost.locationTag ? (
            <PostLocationTag location={originalPost.locationTag} compact />
          ) : null}
        </div>
      ) : null}

      {originalPost.quoteTarget ? (
        <div className="p-2.5 pt-0">
          <FeedEntityQuoteEmbed target={originalPost.quoteTarget} compact />
        </div>
      ) : null}
    </div>
  );
}

function EmbedMediaTile({
  item,
  audioId,
  forceMuted,
}: {
  item: NonNullable<ReturnType<typeof toRenderableMedia>>;
  audioId: string;
  forceMuted: boolean;
}) {
  if (item.provider === "klipy") {
    return item.kind === "video" ? (
      <video
        src={item.url}
        className="absolute inset-0 h-full w-full object-cover"
        playsInline
        muted
        loop
        autoPlay
      />
    ) : (
      // biome-ignore lint/performance/noImgElement: animated KLIPY media must remain animated.
      <img
        src={item.url}
        alt="Referenced post GIF"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  if (item.kind === "video") {
    return (
      <FeedVideo
        audioId={audioId}
        src={item.url}
        mimeType={item.mimeType || "video/mp4"}
        posterUrl={item.poster?.url}
        blurHash={item.blurHash}
        className="absolute inset-0 h-full w-full object-cover"
        forceMuted={forceMuted}
      />
    );
  }
  return (
    <Image
      src={item.url}
      alt="Referenced post media"
      fill
      className="object-cover"
      sizes="(max-width: 768px) 100vw, 520px"
    />
  );
}
